import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  motion,
  useMotionValue,
  useTransform,
  useScroll,
  useSpring,
} from "framer-motion";
import {
  ArrowRight,
  Users,
  Database,
  Shield,
  CheckCircle,
  BarChart3,
  BookOpen,
  Zap,
  Sparkles,
  Globe,
  Target,
} from "lucide-react";

const HomeContainer = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.gradients.background};
  overflow-x: hidden;
  width: 100%;
  position: relative;
`;

const HeroSection = styled.section`
  position: relative;
  padding: 10rem 2rem 6rem;
  text-align: center;
  background: ${({ theme }) => theme.colors.gradients.hero};
  color: ${({ theme }) => theme.colors.text.inverse};
  overflow: hidden;
  width: 100%;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        ellipse at 20% 50%,
        rgba(220, 38, 38, 0.15) 0%,
        transparent 50%
      ),
      radial-gradient(
        ellipse at 80% 20%,
        rgba(153, 27, 27, 0.1) 0%,
        transparent 50%
      );
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 8rem 1.5rem 4rem;
    min-height: 90vh;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 7rem 1rem 3rem;
    min-height: 85vh;
  }
`;

const FloatingElement = styled(motion.div)<{ size: number; delay: number }>`
  position: absolute;
  width: ${({ size }) => size}px;
  height: ${({ size }) => size}px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  border-radius: 50%;
  opacity: 0.1;
  filter: blur(1px);
  z-index: 1;
`;

const ParticleField = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  z-index: 1;
`;

const HeroContent = styled(motion.div)`
  max-width: ${({ theme }) => theme.container.xl};
  width: 100%;
  margin: 0 auto;
  position: relative;
  z-index: 2;
  padding: 0 0.5rem;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(1.75rem, 6vw, 4rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  margin-bottom: 1.5rem;
  color: ${({ theme }) => theme.colors.text.inverse};
  line-height: 1.15;
  position: relative;
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    font-size: clamp(1.5rem, 7vw, 2.25rem);
    margin-bottom: 1rem;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 50%;
    width: 0;
    height: 3px;
    background: ${({ theme }) => theme.colors.gradients.primary};
    transition: width 1s ease 0.5s;
    transform: translateX(-50%);
  }

  &.animate-underline::after {
    width: 200px;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: ${({ theme }) => theme.colors.text.inverse};
  opacity: 0.9;
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  position: relative;
  padding: 0 0.5rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    margin-bottom: 2rem;
  }
`;

const HeroLogoContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  aspect-ratio: 25 / 16;
  margin: 2.5rem auto;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid rgba(220, 38, 38, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  background: rgba(255, 255, 255, 0.95);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 12px 48px rgba(220, 38, 38, 0.15);
    border-color: rgba(220, 38, 38, 0.3);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 400px;
    margin: 2rem auto;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-width: min(100%, 320px);
    margin: 1.5rem auto;
  }
`;

const HeroLogoInner = styled.div`
  position: absolute;
  top: 8%;
  left: 8%;
  right: 8%;
  bottom: 8%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const HeroLogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
`;

const CTAButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1.25rem 2.5rem;
  width: auto;
  max-width: 100%;
  background: ${({ theme }) => theme.colors.gradients.secondary};
  color: ${({ theme }) => theme.colors.text.inverse};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  cursor: pointer;
  transition: all ${({ theme }) => theme.animations.duration.fast}
    ${({ theme }) => theme.animations.easing.ease};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }

  &:hover {
    transform: translateY(-3px) scale(1.02);
    box-shadow: 0 20px 40px rgba(220, 38, 38, 0.3);
  }

  &:active {
    transform: translateY(-1px) scale(1.01);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    max-width: 320px;
    padding: 1rem 1.5rem;
    font-size: ${({ theme }) => theme.typography.sizes.base};
  }
`;

const FeaturesSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const FeaturesContainer = styled.div`
  max-width: ${({ theme }) => theme.container.xl};
  margin: 0 auto;
`;

const SectionTitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  margin-bottom: 4rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const FeatureCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface.primary};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: 2.5rem;
  text-align: center;
  transition: all ${({ theme }) => theme.animations.duration.normal}
    ${({ theme }) => theme.animations.easing.ease};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  position: relative;
  overflow: hidden;
  cursor: pointer;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${({ theme }) => theme.colors.gradients.primary};
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
  }

  &:hover::before {
    opacity: 0.03;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 60px rgba(220, 38, 38, 0.2);
    border-color: ${({ theme }) => theme.colors.accent.primary};
  }

  & > * {
    position: relative;
    z-index: 2;
  }
`;

const FeatureIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background: ${({ theme }) => theme.colors.gradients.primary};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.inverse};
  position: relative;

  &::after {
    content: "";
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: ${({ theme }) => theme.colors.gradients.primary};
    border-radius: ${({ theme }) => theme.borderRadius.xl};
    opacity: 0;
    z-index: -1;
    transition: opacity 0.3s ease;
  }

  ${FeatureCard}:hover &::after {
    opacity: 0.5;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.5;
    }
    50% {
      transform: scale(1.05);
      opacity: 0.3;
    }
    100% {
      transform: scale(1);
      opacity: 0.5;
    }
  }
`;

const FeatureTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  font-weight: ${({ theme }) => theme.typography.weights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  line-height: 1.6;
`;

const StatsSection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.gradients.subtle};
  overflow: hidden;
  width: 100%;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23dc2626' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
      repeat;
    z-index: 1;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const StatsContainer = styled.div`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 3rem;
  text-align: center;
`;

const StatCard = styled(motion.div)`
  padding: 2.5rem 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  border: 1px solid rgba(220, 38, 38, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ theme }) => theme.colors.gradients.primary};
    transform: scaleX(0);
    transition: transform 0.5s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(220, 38, 38, 0.15);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const StatNumber = styled(motion.div)`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.accent.primary};
  margin-bottom: 0.5rem;
  position: relative;

  &::after {
    content: "✨";
    position: absolute;
    top: -10px;
    right: -15px;
    font-size: 0.6em;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  ${StatCard}:hover &::after {
    opacity: 1;
  }
`;

const StatLabel = styled.div`
  font-size: ${({ theme }) => theme.typography.sizes.base};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
`;

const CTASection = styled.section`
  padding: 6rem 2rem;
  background: ${({ theme }) => theme.colors.surface.primary};
  text-align: center;
  overflow-x: hidden;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 5rem 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 4rem 1rem;
  }
`;

const CTAContainer = styled.div`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
`;

const CTATitle = styled(motion.h2)`
  font-size: clamp(2rem, 4vw, 2.5rem);
  font-weight: ${({ theme }) => theme.typography.weights.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 1rem;
`;

const CTADescription = styled(motion.p)`
  font-size: ${({ theme }) => theme.typography.sizes.lg};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 3rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

// Logo Showcase Section - NEW!
const LogoShowcaseSection = styled.section`
  position: relative;
  padding: 8rem 2rem 6rem;
  background: ${({ theme }) => theme.colors.gradients.dark};
  overflow: hidden;
  width: 100%;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
        ellipse at center,
        rgba(220, 38, 38, 0.12) 0%,
        transparent 70%
      ),
      conic-gradient(
        from 0deg at 50% 50%,
        rgba(220, 38, 38, 0.1) 0deg,
        transparent 60deg,
        rgba(153, 27, 27, 0.05) 120deg,
        transparent 180deg
      );
    z-index: 1;
    animation: rotate 20s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1000px;
    height: 1000px;
    background: radial-gradient(
      circle,
      rgba(153, 27, 27, 0.08) 0%,
      transparent 70%
    );
    transform: translate(-50%, -50%);
    z-index: 1;
    animation: pulse 4s ease-in-out infinite;
  }

  @keyframes pulse {
    0%,
    100% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0.8;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.1);
      opacity: 0.4;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 7rem 1.5rem 5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 6rem 1rem 4rem;
  }
`;

const LogoShowcaseContainer = styled(motion.div)`
  max-width: ${({ theme }) => theme.container.lg};
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
`;

const ShowcaseLogo = styled(motion.img)`
  width: 100%;
  max-width: 500px;
  height: auto;
  margin: 0 auto 3rem;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  filter: drop-shadow(0 20px 40px rgba(24, 24, 27, 0.4));
  transition: all 0.4s ease;
  position: relative;
  z-index: 3;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 400px;
    margin: 0 auto 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    max-width: 320px;
    margin: 0 auto 1.5rem;
  }
`;

const LogoTagline = styled(motion.p)`
  font-size: clamp(1.25rem, 3vw, 1.5rem);
  color: ${({ theme }) => theme.colors.text.light};
  font-weight: ${({ theme }) => theme.typography.weights.medium};
  margin-bottom: 2rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const LogoAccentLine = styled(motion.div)`
  width: 100px;
  height: 3px;
  background: ${({ theme }) => theme.colors.gradients.primary};
  margin: 0 auto 2rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
`;

const Home: React.FC = () => {
  const [titleAnimated, setTitleAnimated] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const heroRef = useRef<HTMLElement>(null);

  // Smooth mouse tracking
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const smoothMouseX = useSpring(mouseX, { damping: 50, stiffness: 400 });
  const smoothMouseY = useSpring(mouseY, { damping: 50, stiffness: 400 });

  // Scroll-based animations
  const { scrollYProgress } = useScroll();
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -100]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [mouseX, mouseY]);

  useEffect(() => {
    const timer = setTimeout(() => setTitleAnimated(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  // Floating particles data
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    size: Math.random() * 40 + 20,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 2,
    duration: Math.random() * 10 + 10,
  }));

  const features = [
    {
      icon: <Database size={36} />,
      title: "Data Transformation",
      description:
        "Convert complex educational data into engaging, interactive applications that students and teachers love to use.",
    },
    {
      icon: <Users size={36} />,
      title: "Social Learning Platforms",
      description:
        "Create social media-style educational platforms that boost engagement and improve learning outcomes.",
    },
    {
      icon: <Shield size={36} />,
      title: "Secure & Compliant",
      description:
        "Built with FERPA compliance and educational data privacy standards to keep student information safe.",
    },
    {
      icon: <BarChart3 size={36} />,
      title: "Analytics & Insights",
      description:
        "Gain valuable insights into student engagement and learning patterns with powerful analytics tools.",
    },
    {
      icon: <BookOpen size={36} />,
      title: "Curriculum Integration",
      description:
        "Seamlessly integrate with existing curriculum and learning management systems for easy adoption.",
    },
    {
      icon: <Zap size={36} />,
      title: "Custom Development",
      description:
        "Tailored software solutions designed specifically for your district's unique needs and requirements.",
    },
  ];

  const stats = [
    {
      number: "50+",
      label: "School Districts Served",
      icon: <Globe size={24} />,
    },
    { number: "100K+", label: "Students Engaged", icon: <Users size={24} /> },
    { number: "95%", label: "User Satisfaction", icon: <Target size={24} /> },
    { number: "24/7", label: "Support Available", icon: <Shield size={24} /> },
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const cardHoverVariants = {
    hover: {
      scale: 1.02,
      y: -8,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
  };

  return (
    <HomeContainer>
      <HeroSection ref={heroRef}>
        <HeroContent>
          {/* Title above logo */}
          <HeroTitle>
            Transform Educational Data Into Engaging Applications
          </HeroTitle>

          {/* Logo in the middle */}
          <HeroLogoContainer>
            <HeroLogoInner>
              <HeroLogoImage src="/approved-logo-w.jpg" alt="Swintek Logo" />
            </HeroLogoInner>
          </HeroLogoContainer>

          {/* Subtitle and button below logo */}
          <HeroSubtitle>
            We specialize in creating social media-style educational platforms
            that turn complex data into interactive, engaging experiences for
            students and educators.
          </HeroSubtitle>

          <div style={{ marginTop: "2rem" }}>
            <CTAButton>
              Get Started Today
              <ArrowRight size={20} />
            </CTAButton>
          </div>
        </HeroContent>
      </HeroSection>

      <FeaturesSection>
        <FeaturesContainer>
          <SectionTitle>Why Choose Swintek?</SectionTitle>

          <SectionSubtitle>
            We combine technical excellence with educational expertise to
            deliver solutions that truly make a difference.
          </SectionSubtitle>

          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <FeatureIcon>{feature.icon}</FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesContainer>
      </FeaturesSection>

      <StatsSection>
        <StatsContainer>
          <SectionTitle>Trusted by Educational Leaders</SectionTitle>

          <StatsGrid>
            {stats.map((stat, index) => (
              <StatCard key={index}>
                <div style={{ marginBottom: "1rem", color: "#dc2626" }}>
                  {stat.icon}
                </div>
                <StatNumber>{stat.number}</StatNumber>
                <StatLabel>{stat.label}</StatLabel>
              </StatCard>
            ))}
          </StatsGrid>
        </StatsContainer>
      </StatsSection>

      <CTASection>
        <CTAContainer>
          <CTATitle>Ready to Transform Your Educational Data?</CTATitle>

          <CTADescription>
            Let's discuss how we can help your district create engaging,
            interactive applications that improve student outcomes.
          </CTADescription>

          <div style={{ marginTop: "2rem" }}>
            <CTAButton>
              Schedule a Consultation
              <ArrowRight size={20} />
            </CTAButton>
          </div>
        </CTAContainer>
      </CTASection>
    </HomeContainer>
  );
};

export default Home;
